import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { IconPlus } from "@/components/Icons";
import {
  Button,
  Empty,
  Pill,
  SearchField,
  Segmented,
  Spacer,
  Table,
  Td,
  Th,
  Toolbar,
} from "@/components/kit";
import { displayName } from "@/lib/format";
import { SportGlyph } from "@/lib/sportIcons";

import styles from "./SportsTable.module.css";

export type SportTableRow = {
  /** The sport this one sits inside, if any. */
  partOf?: string | null;
  /** True when it holds disciplines rather than fixtures. */
  groups?: boolean;
  code: string;
  /** Live version number, or null when the sport has never been published. */
  published: number | null;
  /** Open draft version, or null when there are no unpublished changes. */
  draft: number | null;
  /** Already formatted for display — see loadSportRows. */
  publishedAt: string | null;
  fixtures: number;
  masters: number;
  /** Emoji id chosen in onboarding — see `lib/sportIcons`. */
  icon?: string | null;
};

type Status = "all" | "published" | "draft";

const PAGE_SIZE = 12;

/**
 * Every sport in one table: search, a live/draft filter, and pagination once it
 * is worth having.
 *
 * A grid of cards looked fine at four sports and stops working at forty — this is
 * the surface an operator will live in once the platform has real breadth, so it
 * is a list first and pretty second.
 *
 * Rewritten onto the kit, which it predated: it was a bare `<table>` with no
 * classes, so it inherited none of the shared header, alignment or row rules, and
 * its own stylesheet carried a dozen hand-picked hex values and pixel sizes. It
 * read as unstyled rather than plain — headings floating over columns they did not
 * line up with, counts left-aligned under a right-aligned heading, and a bare blue
 * "Edit" link on every row.
 *
 * What the shape says now:
 *
 * * **A name and where it sits are one cell**, on two lines, rather than a name
 *   with pills trailing after it.
 * * **One status, not two.** "Live v1" beside "Draft" read as a contradiction.
 *   The live version *is* the status; an unpublished draft is a note under it,
 *   because it is what is coming rather than what is true.
 * * **Counts line up** — right-aligned under a right-aligned heading, in tabular
 *   figures, so a column can be compared by eye instead of read.
 * * **Nought shows as a dash.** The interesting thing about a sport with no
 *   fixtures is that it has none, and a 0 among 8s and 11s has to be read before
 *   it says so.
 */
export function SportsTable({ rows }: { rows: SportTableRow[] }) {
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState<Status>("all");
  const [page, setPage] = useState(0);

  const counts = useMemo(
    () => ({
      all: rows.length,
      published: rows.filter((r) => r.published !== null).length,
      draft: rows.filter((r) => r.draft !== null).length,
    }),
    [rows],
  );

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return rows.filter((row) => {
      if (status === "published" && row.published === null) return false;
      if (status === "draft" && row.draft === null) return false;
      if (!needle) return true;
      return (
        row.code.toLowerCase().includes(needle) ||
        displayName(row.code).toLowerCase().includes(needle)
      );
    });
  }, [rows, query, status]);

  // Filtering can strip the page out from under us — clamp rather than blank.
  const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const current = Math.min(page, pages - 1);
  const visible = filtered.slice(current * PAGE_SIZE, (current + 1) * PAGE_SIZE);

  /** Narrowing while on page four leaves an empty page four, which reads as
   *  "nothing matched". The reset belongs to the act of narrowing. */
  const narrow = (change: () => void) => {
    change();
    setPage(0);
  };

  /** What a sport is, in one phrase, under its name. */
  const note = (row: SportTableRow): string | null => {
    if (row.groups) return "Holds disciplines";
    if (row.partOf) return `In ${displayName(row.partOf)}`;
    return null;
  };

  return (
    <div className={styles.wrap}>
      <Toolbar>
        <SearchField
          value={query}
          placeholder="Search sports"
          aria-label="Search sports"
          onChange={(e) => narrow(() => setQuery(e.target.value))}
        />
        <Segmented
          name="Filter by status"
          value={status}
          onChange={(next) => narrow(() => setStatus(next))}
          options={[
            { value: "all", label: `All ${counts.all}` },
            { value: "published", label: `Live ${counts.published}` },
            { value: "draft", label: `Draft ${counts.draft}` },
          ]}
        />
        <Spacer />
        <Link to="/sports/new" className={styles.primaryAction}>
          <IconPlus size={15} />
          Add a sport
        </Link>
      </Toolbar>

      {filtered.length === 0 ? (
        <Empty
          title={rows.length === 0 ? "No sports yet" : "Nothing matches"}
          note={
            rows.length === 0
              ? "Onboarding one is what fills every other screen: its masters become the menu, and its kinds of fixture become the entry form."
              : "Widen the search, or choose All."
          }
          action={
            rows.length === 0 ? (
              <Link to="/sports/new" className={styles.primaryAction}>
                Add the first one
              </Link>
            ) : (
              <Button
                onClick={() =>
                  narrow(() => {
                    setQuery("");
                    setStatus("all");
                  })
                }
              >
                Clear
              </Button>
            )
          }
        />
      ) : (
        <>
          <Table>
            <thead>
              <tr>
                <Th>Sport</Th>
                <Th className={styles.colStatus}>Status</Th>
                <Th num className={styles.colCount}>
                  Masters
                </Th>
                <Th num className={styles.colCount}>
                  Fixtures
                </Th>
                <Th className={styles.colDate}>Published</Th>
                <Th className={styles.colAction} aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {visible.map((row) => (
                <tr key={row.code}>
                  <Td>
                    <Link to={`/${row.code}`} className={styles.sport}>
                      <span className={styles.glyph}>
                        <SportGlyph id={row.icon} size={17} />
                      </span>
                      <span className={styles.sportText}>
                        <span className={styles.sportName}>{displayName(row.code)}</span>
                        {note(row) && <span className={styles.sportNote}>{note(row)}</span>}
                      </span>
                    </Link>
                  </Td>
                  <Td>
                    <span className={styles.status}>
                      {row.published !== null ? (
                        <Pill tone="good" dot>
                          Live v{row.published}
                        </Pill>
                      ) : (
                        <Pill tone="warn">Never published</Pill>
                      )}
                      {/* A note, not a second status: a draft is what is coming,
                          not what is true. */}
                      {row.draft !== null && (
                        <span className={styles.statusNote}>draft v{row.draft} pending</span>
                      )}
                    </span>
                  </Td>
                  <Td num quiet={row.masters === 0}>
                    {row.masters || "—"}
                  </Td>
                  <Td num quiet={row.fixtures === 0}>
                    {row.fixtures || "—"}
                  </Td>
                  <Td className={styles.date} quiet={!row.publishedAt}>
                    {row.publishedAt ?? "—"}
                  </Td>
                  <Td className={styles.actions}>
                    {/* `/{code}/setup`, not `/sports/new?from={code}`. Both open
                        the same editor; only one of them says so in the address
                        bar and the breadcrumb. */}
                    <Link to={`/${row.code}/setup`} className={styles.rowAction}>
                      Setup
                    </Link>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>

          {/* Only when there is a second page. A disabled Previous and Next under
              six rows is two controls that can never do anything. */}
          {pages > 1 && (
            <div className={styles.pager}>
              <span className={styles.pageInfo}>
                {current * PAGE_SIZE + 1}–{Math.min(filtered.length, (current + 1) * PAGE_SIZE)} of{" "}
                {filtered.length}
              </span>
              <Spacer />
              <Button disabled={current === 0} onClick={() => setPage(current - 1)}>
                Previous
              </Button>
              <Button disabled={current >= pages - 1} onClick={() => setPage(current + 1)}>
                Next
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
